import React, { memo } from "react";
import { Card, CardContent, Typography, Box, Chip, useTheme } from "@mui/material";
import { TrendingUpRounded as TrendingUpRoundedIcon } from '@mui/icons-material';
import FlagIcon from './FlagIcon';

const CurrencyCard = ({ short, full, sell, buy, flag, onSelect, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasBuyRate = buy && buy.trim() !== '' && buy !== '‌';

  return (
    <Card
      onClick={onSelect}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: onSelect ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        {/* Top Bar: Flag Avatar, Full Name & Short Code Badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FlagIcon short={short} flag={flag} size={28} />
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              {/* Full Name on TOP */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 120,
                }}
              >
                {full || short}
              </Typography>
              {/* Short Name BELOW */}
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'inline-block',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              >
                {short}
              </Typography>
            </Box>
          </Box>

          <Chip
            size="small"
            label={flag === '🪙' ? (t ? t('goldTag') : 'Gold') : (t ? t('fxTag') : 'FX')}
            sx={{
              fontWeight: 700,
              fontSize: '0.675rem',
              height: 22,
              px: 1,
              flexShrink: 0,
              backgroundColor: flag === '🪙'
                ? (isDark ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7')
                : (isDark ? 'rgba(59, 130, 246, 0.15)' : '#DBEAFE'),
              color: flag === '🪙' ? '#F59E0B' : '#3B82F6',
              border: '1px solid',
              borderColor: flag === '🪙'
                ? (isDark ? 'rgba(245, 158, 11, 0.3)' : '#FDE68A')
                : (isDark ? 'rgba(59, 130, 246, 0.3)' : '#BFDBFE'),
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiChip-label': {
                padding: '0 !important',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              },
            }}
          />
        </Box>

        {/* Pricing Grid */}
        <Box
          sx={{
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          {/* Sell / Main Price */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {t ? t('sellRate') : 'Sell Rate'}
              <TrendingUpRoundedIcon sx={{ fontSize: 13, color: 'secondary.main' }} />
            </Typography>
            <Typography
              variant="h6"
              className="mono-num"
              sx={{
                fontWeight: 800,
                fontSize: sell && sell.length > 9 ? '0.95rem' : '1.1rem',
                color: 'text.primary',
                mt: 0.25,
              }}
            >
              {sell || '-'}
            </Typography>
          </Box>

          {/* Buy Price if available */}
          {hasBuyRate && (
            <Box sx={{ textAlign: 'end' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {t ? t('buyRate') : 'Buy Rate'}
              </Typography>
              <Typography
                variant="body2"
                className="mono-num"
                sx={{
                  fontWeight: 700,
                  fontSize: buy && buy.length > 9 ? '0.85rem' : '0.95rem',
                  color: 'info.main',
                  mt: 0.25,
                }}
              >
                {buy}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default memo(CurrencyCard);
